import api from '../api/axios';
const API_URL = process.env.REACT_APP_API_URL;
/**
 * Thin wrapper around the RBAC/PBAC access-control API (Phase 1 backend
 * foundation). All calls are session-cookie authenticated via the shared
 * axios instance (withCredentials: true, baseURL from REACT_APP_API_BASE_URL).
 */
const accessService = {
  // Canonical resource/action registry - drives <PermissionMatrix />.
  getResources: async (module) => {
    const res = await api.get(`${API_URL}api/access/resources/`, { params: module ? { module } : {} });
    return res.data.resources || [];
  },

  // Effective permission bundle for the logged-in user.
  getMyAccess: async () => {
    const res = await api.get(`${API_URL}api/access/me/`);
    return res.data;
  },

  // Simulate effective access for a role + optional overrides, without
  // persisting anything - backs <EffectiveAccessPreview />.
  // includeRoleDefaults: also count a policy-less role's legacy capabilities.
  // Only for seeding a new user's grants - they are not part of what the
  // backend enforces, so never use them as a "granted by role" baseline.
  previewAccess: async (roleName, overrideGrants = [], { includeRoleDefaults = false } = {}) => {
    const res = await api.post(`${API_URL}api/access/preview/`, {
      role_name: roleName,
      override_grants: overrideGrants,
      include_role_defaults: includeRoleDefaults,
    });
    return res.data.permissions || {};
  },

  // Role/user policy baseline + override management.
  listPolicies: async (adminId) => {
    const res = await api.get(`${API_URL}api/access/policies/`, { params: adminId ? { admin_id: adminId } : {} });
    return res.data.policies || [];
  },
  createPolicy: async (policyName, grants, dataScope = {}, adminId) => {
    const res = await api.post(`${API_URL}api/access/policies/`, {
      policy_name: policyName,
      grants,
      data_scope: dataScope,
      admin_id: adminId,
    });
    return res.data;
  },
  updatePolicy: async (policyId, fields) => {
    const res = await api.put(`${API_URL}api/access/policies/`, { policy_id: policyId, ...fields });
    return res.data;
  },
  deletePolicy: async (policyId) => {
    const res = await api.delete(`${API_URL}api/access/policies/`, { params: { policy_id: policyId } });
    return res.data;
  },

  listAssignments: async (scopeType, scopeRef) => {
    const params = {};
    if (scopeType) params.scope_type = scopeType;
    if (scopeRef) params.scope_ref = scopeRef;
    const res = await api.get(`${API_URL}api/access/policy-assignments/`, { params });
    return res.data.assignments || [];
  },
  assignPolicy: async (scopeType, scopeRef, policyId, mode = 'extend') => {
    const res = await api.post(`${API_URL}api/access/policy-assignments/`, {
      scope_type: scopeType,
      scope_ref: scopeRef,
      policy_id: policyId,
      mode,
    });
    return res.data;
  },
  removeAssignment: async (assignmentId) => {
    const res = await api.delete(`${API_URL}api/access/policy-assignments/` , { params: { assignment_id: assignmentId } });
    return res.data;
  },

  // One-stop "Manage Permissions" endpoint for a specific user (by uid):
  // role baseline + current override + merged effective bundle in one call.
  getUserPermissions: async (uid) => {
    const res = await api.get(`${API_URL}api/access/user-permissions/`, { params: { uid } });
    return res.data;
  },
  // options.suppressScopeAlert: true skips the backend's Business Unit/
  // Location "change" alert email - pass this from user CREATION (there's
  // no previous scope to compare against, so every BU/Location picked would
  // otherwise look like a change to alert on), never from an edit of an
  // already-existing user.
  saveUserPermissionOverride: async (uid, mode, overrideGrants, dataScope = {}, options = {}) => {
    const res = await api.post(`${API_URL}api/access/user-permissions/`, {
      uid,
      mode,
      override_grants: overrideGrants,
      data_scope: dataScope,
      suppress_scope_alert: !!options.suppressScopeAlert,
      org_scope_type: options.orgScopeType || '',
    });
    return res.data;
  },

  // The Organisational Scope step's Business Unit/Location picks are a
  // single, user-level "which BUs/locations does this person belong to"
  // selection - they should apply to every resource that declares those as
  // scope_fields (Document Vault, Risk Register, TPRM, etc.), not just
  // whichever resource was first wired up. Returns a data_scope-overrides
  // dict ready to merge into the payload for saveUserPermissionOverride,
  // keyed by resource_id, containing only the fields each resource actually
  // declares.
  //
  // existingOverrides (the in-progress dataScopeOverrides state, e.g. TPIN
  // entries set via the Permission Matrix's DataScopeControl) is passed
  // through so a resource scoped by MORE than one dimension at once (e.g.
  // tprm.third_party_users: TPIN + business_unit + location) keeps its
  // other-field entries intact instead of the caller's naive
  // `{...dataScopeOverrides, ...orgScopeOverrides}` spread wholesale
  // replacing them - orgScopeOverrides used to build a fresh BU/location-only
  // list per resource, silently dropping any TPIN entry for that same
  // resource_id.
  //
  // pairs (Organisational Scope Step type E - "Multiple Business Units +
  // Multiple Locations") is an explicit BU->Location authorization list:
  // [{business_unit, locations: [...]}]. When present, a resource scoped by
  // BOTH business_unit and location gets a single "pairs" data_scope entry
  // (see query_scope.py) instead of two independent "values" entries -
  // independent entries would grant the full BU x Location cross-product
  // (e.g. HR-Mumbai, even if the admin only ever authorized HR-Delhi and
  // Finance-Mumbai), which is exactly what the pairs shape exists to avoid.
  // A resource scoped by only ONE of those two fields still gets a plain
  // "values" entry, built from the union of BUs/locations across all pairs.
  buildOrgScopeOverrides: async (selectedBusinessUnits = [], selectedLocations = [], existingOverrides = {}, pairs = [], preFetchedResources = null) => {
    const hasPairs = Array.isArray(pairs) && pairs.length > 0;
    if (!selectedBusinessUnits.length && !selectedLocations.length && !hasPairs) return {};
    // Callers that already fetched the resource registry this same save
    // (e.g. to derive capabilities[] from the permission matrix) can pass it
    // in directly instead of this making its own redundant round trip.
    let resources = preFetchedResources;
    if (!resources) {
      try {
        resources = await accessService.getResources();
      } catch (err) {
        console.error('Failed to fetch access resources for org-scope propagation:', err);
        return {};
      }
    }
    const overrides = {};
    resources.forEach((resource) => {
      const fields = resource.scope_fields || [];
      const hasBuField = fields.includes('business_unit');
      const hasLocField = fields.includes('location');
      const entry = [];

      if (hasPairs && hasBuField && hasLocField) {
        entry.push({
          field: 'business_unit_location',
          match: 'pairs',
          pairs: pairs.map((p) => ({ business_unit: p.business_unit, locations: p.locations || [] })),
        });
      } else {
        const buValues = hasPairs ? pairs.map((p) => p.business_unit).filter(Boolean) : selectedBusinessUnits;
        const locValues = hasPairs ? [...new Set(pairs.flatMap((p) => p.locations || []))] : selectedLocations;
        if (hasBuField && buValues.length) {
          entry.push({ field: 'business_unit', match: 'values', values: buValues });
        }
        if (hasLocField && locValues.length) {
          entry.push({ field: 'location', match: 'values', values: locValues });
        }
      }
      if (!entry.length) return;

      const existing = existingOverrides[resource.resource_id];
      const existingList = Array.isArray(existing) ? existing : (existing ? [existing] : []);
      const preserved = existingList.filter((e) => e && e.field !== 'business_unit' && e.field !== 'location' && e.field !== 'business_unit_location');

      overrides[resource.resource_id] = [...preserved, ...entry];
    });
    return overrides;
  },

  // ABAC login conditions (IP allowlist, allowed weekdays, daily time
  // window, valid date range) - scopeRef is a role_id for scopeType "role",
  // or a user's `uid` for scopeType "user" (resolved to _id server-side).
  getAccessConditions: async (scopeType, scopeRef) => {
    if (!scopeRef) return null;
    const res = await api.get(`${API_URL}api/access/conditions/`, { params: { scope_type: scopeType, scope_ref: scopeRef } });
    return res.data.condition || null;
  },
  saveAccessConditions: async (scopeType, scopeRef, fields) => {
    const res = await api.post(`${API_URL}api/access/conditions/`, {
      scope_type: scopeType,
      scope_ref: scopeRef,
      ...fields,
    });
    return res.data;
  },
  removeAccessConditions: async (scopeType, scopeRef) => {
    const res = await api.delete(`${API_URL}api/access/conditions/`, { params: { scope_type: scopeType, scope_ref: scopeRef } });
    return res.data;
  },
};

/**
 * Turns a resource's raw data_scope (from getDataScope()/api/access/me/)
 * into the shape a Business Unit/Location filter or create-form picker
 * actually needs. A resource scoped by BOTH fields at once can come back in
 * either shape depending on how the admin authorised it (see
 * OrganisationalScopeStep.jsx / query_scope.py):
 *   - flat "values" entries (independent BU list + independent Location list)
 *   - a single "pairs" entry (explicit BU->Locations authorisation list)
 * Callers that only ever looked for the "values" shape silently fell back to
 * the full org master list for any pairs-scoped user - the exact same "show
 * every value for every BU" cross-product bug pairs mode exists to prevent,
 * just relocated to a dropdown instead of a query filter.
 *
 * Returns { businessUnits, locations, pairs, isPairs }. `businessUnits`/
 * `locations` are always the flat lists a simple (non-dependent) picker can
 * bind to; `pairs` (only non-empty when isPairs) is
 * [{business_unit, locations}] for callers that want to narrow Location
 * options by the currently-selected Business Unit. `businessUnits`/
 * `locations` both empty AND isPairs false means "no restriction on this
 * resource" - the caller should fall back to the full master list, same as
 * an empty "values" entry always meant.
 */
export const resolveScopedBuLocationOptions = (dataScope) => {
  const scopeList = Array.isArray(dataScope) ? dataScope : (dataScope ? [dataScope] : []);
  const pairsEntry = scopeList.find((s) => s?.field === 'business_unit_location' && s?.match === 'pairs');
  if (pairsEntry?.pairs?.length) {
    const pairs = pairsEntry.pairs.map((p) => ({ business_unit: p.business_unit, locations: p.locations || [] }));
    return {
      businessUnits: pairs.map((p) => p.business_unit).filter(Boolean),
      locations: [...new Set(pairs.flatMap((p) => p.locations))],
      pairs,
      isPairs: true,
    };
  }
  const businessUnitScope = scopeList.find((s) => s?.field === 'business_unit' && s?.match === 'values');
  const locationScope = scopeList.find((s) => s?.field === 'location' && s?.match === 'values');
  return {
    businessUnits: businessUnitScope?.values || [],
    locations: locationScope?.values || [],
    pairs: [],
    isPairs: false,
  };
};

export default accessService;
